// Journal d'audit (G05, charte D-038) : filtres serveur (ressource, action), tri rapide par type d'action
// sur les entrées chargées, tableau à lignes sélectionnables et détail de l'événement choisi.
// Le journal est en écriture seule : aucune entrée ne peut être modifiée ni supprimée.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { MessageErreur, Squelette } from '../../composants/Etats';
import { dateHeure } from '../../composants/format';
import { montrerSurPetitEcran } from '../../composants/selection';
import { CadreAdministration } from './Cadre';

interface LigneAudit {
  id: string;
  horodatage: string;
  acteurCourriel: string | null;
  acteurId: string | null;
  action: string;
  ressource: string;
  ressourceId: string | null;
  adresse: string | null;
  details: unknown;
}

const libelleAction = (a: string) => {
  const t = a.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

function DetailsEvenement({ details }: { details: unknown }) {
  if (details === null || details === undefined) return <p className="attenue">Aucun détail complémentaire.</p>;
  if (typeof details === 'object' && !Array.isArray(details)) {
    const entrees = Object.entries(details as Record<string, unknown>);
    if (entrees.length === 0) return <p className="attenue">Aucun détail complémentaire.</p>;
    return (
      <dl className="paires">
        {entrees.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return <p>{String(details)}</p>;
}

export function JournalAudit() {
  const [ressource, setRessource] = useState('');
  const [action, setAction] = useState('');
  const [avant, setAvant] = useState<string | null>(null);
  const [type, setType] = useState('');
  const [choisie, setChoisie] = useState<string | null>(null);
  const r = useQuery({
    queryKey: ['audit', ressource, action, avant],
    queryFn: async () => {
      const p = new URLSearchParams({ limite: '100' });
      if (ressource) p.set('ressource', ressource);
      if (action) p.set('action', action);
      if (avant) p.set('avant', avant);
      return api<LigneAudit[]>(`/api/v1/audit?${p.toString()}`);
    },
  });
  const lignes = r.data?.donnees ?? [];
  const types = [...new Set(lignes.map((l) => l.action))].sort();
  const visibles = lignes.filter((l) => !type || l.action === type);
  const selection = visibles.find((l) => l.id === choisie) ?? visibles[0] ?? null;
  const acteurs = new Set(lignes.map((l) => l.acteurCourriel ?? l.acteurId ?? 'système')).size;

  return (
    <CadreAdministration
      titre="Journal d’audit"
      contexte="Administration, traçabilité de toutes les opérations"
      action={
        r.data && (
          <div className="compteurs compteurs-tete" role="list" aria-label="Synthèse des entrées chargées">
            <div role="listitem">
              Entrées chargées<b>{lignes.length}</b>
            </div>
            <div role="listitem">
              Acteurs distincts<b>{acteurs}</b>
            </div>
          </div>
        )
      }
    >
      <form className="barre-filtres" onSubmit={(e) => e.preventDefault()} aria-label="Filtres du journal">
        <div className="champ">
          <label htmlFor="j-ressource">Ressource</label>
          <input
            id="j-ressource"
            value={ressource}
            onChange={(e) => {
              setRessource(e.target.value.trim());
              setAvant(null);
            }}
            placeholder="installations, documents, sessions"
          />
        </div>
        <div className="champ">
          <label htmlFor="j-action">Action</label>
          <input
            id="j-action"
            value={action}
            onChange={(e) => {
              setAction(e.target.value.trim());
              setAvant(null);
            }}
            placeholder="connexion, validation"
          />
        </div>
        {types.length > 1 && (
          <div className="puces filtres-puces" role="radiogroup" aria-label="Type d’action parmi les entrées chargées">
            <button type="button" role="radio" className="puce" aria-checked={type === ''} onClick={() => setType('')}>
              Tout <span className="compte">{lignes.length}</span>
            </button>
            {types.map((t) => (
              <button key={t} type="button" role="radio" className="puce" aria-checked={type === t} onClick={() => setType(t)}>
                {libelleAction(t)} <span className="compte">{lignes.filter((l) => l.action === t).length}</span>
              </button>
            ))}
          </div>
        )}
      </form>
      {r.isLoading && <Squelette lignes={8} />}
      {r.error && <MessageErreur erreur={r.error} />}
      {r.data && (
        <div className="deux-colonnes">
          <section className="principal" aria-label="Événements">
            <div className="tableau-conteneur">
              <table className="tableau lignes-48">
                <caption className="visuellement-cache">Événements du journal d’audit</caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Acteur</th>
                    <th scope="col">Action</th>
                    <th scope="col" className="masquable">
                      Ressource
                    </th>
                    <th scope="col" className="masquable">
                      Adresse
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.length === 0 && (
                    <tr>
                      <td colSpan={5} className="attenue">
                        Aucun événement pour ce filtre.
                      </td>
                    </tr>
                  )}
                  {visibles.map((l) => (
                    <tr key={l.id} aria-selected={selection?.id === l.id}>
                      <td className="horodatage">{dateHeure(l.horodatage)}</td>
                      <td>
                        <button type="button" className="bouton-ligne" aria-pressed={selection?.id === l.id} onClick={() => { setChoisie(l.id); montrerSurPetitEcran('titre-evenement'); }}>
                          {l.acteurCourriel ?? l.acteurId ?? 'système'}
                        </button>
                      </td>
                      <td>
                        <span className="etiquette">{libelleAction(l.action)}</span>
                      </td>
                      <td className="masquable">
                        {l.ressource}
                        {l.ressourceId && <small>{l.ressourceId.slice(0, 13)}</small>}
                      </td>
                      <td className="masquable">{l.adresse ?? <span className="attenue">sans objet</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="tableau-pied">
                <span>
                  {visibles.length} événement{visibles.length > 1 ? 's' : ''} affiché{visibles.length > 1 ? 's' : ''}, journal en lecture seule
                </span>
                {typeof r.data.meta.suite === 'string' && (
                  <button type="button" className="bouton" onClick={() => setAvant(r.data!.meta.suite as string)}>
                    Entrées plus anciennes
                  </button>
                )}
              </div>
            </div>
          </section>
          {selection && (
            <aside className="lateral collant" aria-labelledby="titre-evenement">
              <section className="carte-bloc resume-installation">
                <span className="sur-titre">Détail de l’événement</span>
                <h2 id="titre-evenement">{libelleAction(selection.action)}</h2>
                <dl className="paires">
                  <div>
                    <dt>Date</dt>
                    <dd>{dateHeure(selection.horodatage)}</dd>
                  </div>
                  <div>
                    <dt>Acteur</dt>
                    <dd>{selection.acteurCourriel ?? selection.acteurId ?? 'système'}</dd>
                  </div>
                  <div>
                    <dt>Ressource</dt>
                    <dd>
                      {selection.ressource}
                      {selection.ressourceId && <small>{selection.ressourceId}</small>}
                    </dd>
                  </div>
                  <div>
                    <dt>Adresse</dt>
                    <dd>{selection.adresse ?? 'sans objet'}</dd>
                  </div>
                </dl>
                <h3>Détails</h3>
                <DetailsEvenement details={selection.details} />
                <p className="note">Les entrées du journal ne peuvent être ni modifiées ni supprimées. Fusions, validations, changements de droits et de paramètres y apparaissent automatiquement.</p>
              </section>
            </aside>
          )}
        </div>
      )}
    </CadreAdministration>
  );
}
